// src/components/dashboard/cases/newCase/case_id/shippingSection.js
// OPTIMIZED: caseData prop se data leta hai, apni API hit nahi karta

import React, { useState, useEffect, useRef } from "react";
import { FaPencilAlt } from "react-icons/fa";
import axios from "axios";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { useCurrency } from "../../../../../context/currencyContext";

// ============================================
// HELPER: delivery_date se reschedule option infer karo
// ============================================
const inferRescheduleOption = (deliveryDate) => {
  if (!deliveryDate) return "Keep Current";
  try {
    const d = new Date(deliveryDate);
    if (isNaN(d)) return "Keep Current";
    if (isToday(d)) return "Ship Today";
    if (isTomorrow(d)) return "Ship Next Business Day";
    return "Other Date";
  } catch {
    return "Keep Current";
  }
};

// Helper: date ko yyyy-MM-dd format mein convert karo (input[type=date] ke liye)
const toInputDate = (dateValue) => {
  if (!dateValue) return "";
  try {
    const d = new Date(dateValue);
    if (isNaN(d)) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

const Shipping = ({ caseId, caseData: propCaseData, onShippingUpdate }) => {
  const { formatAmount } = useCurrency();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localCaseData, setLocalCaseData] = useState(propCaseData);
  const [selectedOption, setSelectedOption] = useState("");
  const [isOtherDateSelected, setIsOtherDateSelected] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedShipper, setSelectedShipper] = useState("");
  const [trackNumber, setTrackNumber] = useState("");
  const [shippingCost, setShippingCost] = useState("");

  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(true);
  const [userLabId, setUserLabId] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);

  // ✅ Two separate refs for two dropdowns
  const rescheduleDropdownRef = useRef(null);
  const shipperDropdownRef = useRef(null);

  // Sync prop caseData with local state
  useEffect(() => {
    if (propCaseData) setLocalCaseData(propCaseData);
  }, [propCaseData]);

  // ============================================
  // USER LAB ID
  // ============================================
  useEffect(() => {
    const storedUserLabId = localStorage.getItem("userLabId");
    if (storedUserLabId) setUserLabId(storedUserLabId);
  }, []);

  // ============================================
  // CLOSE DROPDOWN OUTSIDE CLICK
  // ============================================
  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideReschedule =
        rescheduleDropdownRef.current &&
        rescheduleDropdownRef.current.contains(event.target);
      const clickedInsideShipper =
        shipperDropdownRef.current &&
        shipperDropdownRef.current.contains(event.target);

      if (!clickedInsideReschedule && !clickedInsideShipper) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // ============================================
  // SHIPPING TYPES - sirf ek baar fetch (static data)
  // ============================================
  useEffect(() => {
    const fetchShippingTypes = async () => {
      setShippingLoading(true);
      try {
        const storedUserLabId = localStorage.getItem("userLabId");
        const res = await axios.get("https://hms.backend.hiragroupcloud.com/api/shipping", {
          params: { addedBy: storedUserLabId },
        });
        if (res.data.success) {
          const options = res.data.data
            .map((st) => ({
              id: st._id,
              label: st.service ? `${st.company} - ${st.service}` : st.company,
              price: Number(st.price) || 0,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setShippingOptions(options);
        }
      } catch (err) {
        console.error("Error fetching shipping types:", err);
      } finally {
        setShippingLoading(false);
      }
    };
    fetchShippingTypes();
  }, []);

  // ============================================
  // ✅ SYNC LOCAL STATE WITH CASE DATA (MODAL FIELDS)
  // Yeh tab bhi chalega jab propCaseData update ho (confirm ke baad)
  // ============================================
  useEffect(() => {
    if (!localCaseData) return;

    // Shipper / Track / Cost
    setSelectedShipper(localCaseData.shipper || "No Shipper");
    setTrackNumber(localCaseData.shipmentTrackingUrls?.[0] || "");
    setShippingCost(localCaseData.shippingCost ?? "");

    // ✅ Reschedule option reverse-engineer from delivery_date
    const inferredOption = inferRescheduleOption(localCaseData.delivery_date);
    setSelectedOption(inferredOption);
    setIsOtherDateSelected(inferredOption === "Other Date");
    setSelectedDate(
      inferredOption === "Other Date"
        ? toInputDate(localCaseData.delivery_date)
        : ""
    );
  }, [localCaseData]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleOptionChange = (option) => {
    setSelectedOption(option);
    setIsOtherDateSelected(option === "Other Date");
    if (option !== "Other Date") setSelectedDate("");
    setOpenDropdown(null);
  };

  const handleDateChange = (e) => setSelectedDate(e.target.value);
  const handleShipperChange = (value) => {
    setSelectedShipper(value);
    setOpenDropdown(null);
  };
  const handleTrackNumberChange = (e) => setTrackNumber(e.target.value);
  const handleShippingCostChange = (e) => setShippingCost(e.target.value);

  // ============================================
  // CONFIRM - sirf PUT hit + parent refresh
  // ============================================
  const handleConfirm = async () => {
    try {
      const updateData = {};

      if (selectedOption === "Ship Today") {
        updateData.delivery_date = new Date().toISOString();
      } else if (selectedOption === "Ship Next Business Day") {
        const nextDay = new Date();
        nextDay.setDate(nextDay.getDate() + 1);
        updateData.delivery_date = nextDay.toISOString();
      } else if (selectedOption === "Other Date" && selectedDate) {
        const parsedDate = new Date(selectedDate);
        if (!isNaN(parsedDate))
          updateData.delivery_date = parsedDate.toISOString();
        else {
          alert("Please select a valid date");
          return;
        }
      }

      if (selectedShipper && selectedShipper !== "No Shipper")
        updateData.shipper = selectedShipper;
      if (trackNumber) updateData.trackNumber = trackNumber;
      if (shippingCost !== "" && shippingCost !== null)
        updateData.shippingCost = parseFloat(shippingCost) || 0;

      await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/shipping`,
        updateData
      );

      // ✅ Parent ko refresh karne ko bolo
      if (onShippingUpdate) await onShippingUpdate();

      alert("Shipping details updated successfully");
      closeModal();
    } catch (error) {
      console.error(
        "❌ Error updating shipping details:",
        error.response?.data || error.message
      );
      alert(
        "Error updating shipping details: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  // ============================================
  // ✅ OPEN MODAL - har field ko caseData se prefill karo
  // ============================================
  const openModal = () => {
    setIsModalOpen(true);
    setOpenDropdown(null);

    // Shipper / Track / Cost
    setSelectedShipper(localCaseData?.shipper || "No Shipper");
    setTrackNumber(localCaseData?.shipmentTrackingUrls?.[0] || "");
    setShippingCost(localCaseData?.shippingCost ?? "");

    // ✅ Reschedule option infer from delivery_date
    const inferredOption = inferRescheduleOption(localCaseData?.delivery_date);
    setSelectedOption(inferredOption);
    setIsOtherDateSelected(inferredOption === "Other Date");
    setSelectedDate(
      inferredOption === "Other Date"
        ? toInputDate(localCaseData?.delivery_date)
        : ""
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setOpenDropdown(null);
    setSelectedOption("");
    setIsOtherDateSelected(false);
    setSelectedDate("");
  };

  // ============================================
  // DISPLAY HELPERS
  // ============================================
  const deliveryDate =
    localCaseData?.delivery_date && !isNaN(new Date(localCaseData.delivery_date))
      ? format(parseISO(localCaseData.delivery_date), "dd MMM yyyy")
      : "Not Set";

  const deliveredDateTime =
    localCaseData?.delivered_date && !isNaN(new Date(localCaseData.delivered_date))
      ? format(parseISO(localCaseData.delivered_date), "dd MMM yyyy - HH:mm")
      : "Not Delivered";

  const shipper = localCaseData?.shipper || "No Shipper";
  const trackNumberDisplay = localCaseData?.shipmentTrackingUrls?.[0] || "Not Set";
  const shippingCostDisplay = localCaseData?.shippingCost
    ? formatAmount(Number(localCaseData.shippingCost))
    : "Not Set";

  const rescheduleOptions = [
    "Keep Current",
    "Ship Today",
    "Ship Next Business Day",
    "Other Date",
  ];
  const fallbackShippingOptions = [
    "No Shipper",
    "Local driver",
    "Royal Mail",
    "OTHER- UPS COURIER",
  ];

  const shipperOptions = shippingLoading
    ? ["No Shipper", "Loading shipping options..."]
    : shippingOptions.length === 0
    ? fallbackShippingOptions
    : ["No Shipper", ...shippingOptions.map((opt) => opt.label)];

  // ============================================
  // STYLES
  // ============================================
  const sectionWrapperStyle = {
    margin: "0 0 18px 0",
    padding: "0",
    textAlign: "left",
  };
  const labelStyle = {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "18px",
    fontWeight: "bold",
    color: "#000",
    margin: "0 0 4px 0",
    padding: "0",
    lineHeight: "1.3",
    textAlign: "left",
  };
  const valueStyle = {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "17px",
    fontWeight: "normal",
    color: "#000",
    margin: "0",
    padding: "0",
    lineHeight: "1.3",
    textAlign: "left",
  };
  const headingStyle = {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "32px",
    fontWeight: "400",
    color: "#000",
    margin: "0",
    padding: "0",
    lineHeight: "1.2",
    textAlign: "left",
  };

  return (
    <div
      style={{
        padding: "0",
        margin: "0",
        fontFamily: '"Times New Roman", Times, serif',
        color: "#000",
        textAlign: "left",
      }}
    >
      <style>
        {`
          .shipping-edit-button { display: flex !important; align-items: center !important; justify-content: center !important; box-sizing: border-box !important; background: transparent !important; border: none !important; outline: none !important; box-shadow: none !important; border-radius: 0 !important; color: #004aad !important; }
          .shipping-edit-button svg { font-size: 15px !important; width: 15px !important; height: 15px !important; margin: 0 !important; color: #004aad !important; fill: #004aad !important; }
          .shipping-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
          .shipping-modal { background: #fff; padding: 20px; border-radius: 8px; min-width: 600px; text-align: left; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); border: 1px solid #ccc; }
          .shipping-modal-header { background-color: #004aad; padding: 10px; border-bottom: 1px solid #ccc; display: flex; justify-content: space-between; align-items: center; border-radius: 6px; }
          .shipping-modal-title { margin: 0; color: white; }
          .shipping-modal-close { background: none; border: none; cursor: pointer; font-size: 22px; color: white; }
          .shipping-modal-content { padding: 15px; }
          .shipping-details-title { color: #305f9cff; margin: 10px 0; }
          .shipping-fields-container { background-color: #f5f5f5; padding: 10px; border-top: 3px solid #004aad; }
          .shipping-field { margin-bottom: 10px; }
          .shipping-field input { width: 100%; padding: 5px; margin-top: 5px; box-sizing: border-box; }
          .shipping-modal-footer { display: flex; justify-content: flex-end; margin-top: 20px; }
          .shipping-modal-button { background-color: #004aad; border: none; padding: 8px 15px; border-radius: 4px; cursor: pointer; color: white; }
          .shipping-cancel-button { margin-right: 10px; }
          .shipping-custom-dropdown { position: relative; width: 100%; margin-top: 5px; box-sizing: border-box; }
          .shipping-dropdown-trigger { width: 100%; min-height: 31px; padding: 5px 30px 5px 8px; background: #fff; border: 1px solid #767676; border-radius: 2px; cursor: pointer; box-sizing: border-box; text-align: left; position: relative; font-size: 14px; line-height: 1.2; color: #212529; }
          .shipping-dropdown-trigger:focus { outline: none; border-color: #004aad; box-shadow: 0 0 0 1px rgba(0, 74, 173, 0.2); }
          .shipping-dropdown-arrow { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); font-size: 12px; pointer-events: none; }
          .shipping-dropdown-menu { position: absolute; top: calc(100% + 2px); left: 0; width: 100%; background: #fff; border: 1px solid #999; border-radius: 2px; box-shadow: 0 3px 8px rgba(0, 0, 0, 0.18); z-index: 99999; max-height: 220px; overflow-y: auto; overflow-x: hidden; box-sizing: border-box; }
          .shipping-dropdown-option { width: 100%; padding: 7px 8px; background: #fff; border: none; border-bottom: 1px solid #eeeeee; text-align: left; cursor: pointer; font-size: 14px; line-height: 1.2; color: #212529; box-sizing: border-box; }
          .shipping-dropdown-option:last-child { border-bottom: none; }
          .shipping-dropdown-option:hover { background: #f1f1f1; }
          .shipping-dropdown-option.selected { background: #e8f0ff; }
          @media (max-width: 768px) {
            .shipping-edit-button { width: 20px !important; min-width: 20px !important; max-width: 20px !important; height: 18px !important; min-height: 18px !important; max-height: 18px !important; padding: 0 !important; margin-left: 6px !important; margin-top: 2px !important; background: transparent !important; border: none !important; outline: none !important; box-shadow: none !important; color: #004aad !important; }
            .shipping-edit-button svg { font-size: 9px !important; width: 9px !important; height: 9px !important; margin: 0 !important; color: #004aad !important; fill: #004aad !important; }
            .shipping-modal-overlay { width: 100vw !important; height: 100dvh !important; padding: 4px !important; box-sizing: border-box !important; overflow: hidden !important; align-items: center !important; justify-content: center !important; }
            .shipping-modal { width: calc(100vw - 8px) !important; min-width: 0 !important; max-width: calc(100vw - 8px) !important; max-height: calc(100dvh - 8px) !important; padding: 6px !important; margin: 0 !important; border-radius: 5px !important; overflow-y: auto !important; overflow-x: hidden !important; box-sizing: border-box !important; }
            .shipping-modal-header { padding: 5px 7px !important; border-radius: 4px !important; min-height: 28px !important; box-sizing: border-box !important; }
            .shipping-modal-title { font-size: 13px !important; line-height: 1.1 !important; }
            .shipping-modal-close { font-size: 17px !important; line-height: 15px !important; padding: 0 !important; margin: 0 !important; }
            .shipping-modal-content { padding: 5px !important; }
            .shipping-details-title { font-size: 11px !important; line-height: 1.1 !important; margin: 4px 0 !important; }
            .shipping-fields-container { padding: 5px !important; border-top-width: 2px !important; }
            .shipping-field { margin-bottom: 5px !important; }
            .shipping-field label { display: block !important; font-size: 9px !important; line-height: 1.1 !important; margin: 0 !important; padding: 0 !important; }
            .shipping-field input { width: 100% !important; font-size: 9px !important; line-height: 1.1 !important; padding: 3px 4px !important; margin-top: 2px !important; min-height: 22px !important; height: 22px !important; box-sizing: border-box !important; }
            .shipping-custom-dropdown { margin-top: 2px !important; }
            .shipping-dropdown-trigger { width: 100% !important; min-height: 22px !important; height: 22px !important; padding: 3px 24px 3px 4px !important; font-size: 9px !important; line-height: 1.1 !important; border-radius: 2px !important; box-sizing: border-box !important; }
            .shipping-dropdown-arrow { right: 5px !important; font-size: 8px !important; }
            .shipping-dropdown-menu { top: calc(100% + 1px) !important; left: 0 !important; width: 100% !important; max-height: 160px !important; z-index: 999999 !important; box-sizing: border-box !important; }
            .shipping-dropdown-option { width: 100% !important; padding: 4px 5px !important; font-size: 9px !important; line-height: 1.2 !important; min-height: 20px !important; box-sizing: border-box !important; }
            .shipping-field input[type="date"] { font-size: 9px !important; padding: 2px 3px !important; }
            .shipping-fields-container p { font-size: 8px !important; line-height: 1.15 !important; margin-top: 2px !important; margin-bottom: 2px !important; }
            .shipping-status { margin-bottom: 5px !important; padding: 5px !important; border-radius: 3px !important; }
            .shipping-status p { font-size: 8px !important; line-height: 1.15 !important; margin: 0 !important; }
            .shipping-status p:first-child { margin-bottom: 2px !important; }
            .shipping-modal-footer { margin-top: 7px !important; display: flex !important; justify-content: flex-end !important; }
            .shipping-modal-button { font-size: 9px !important; line-height: 1 !important; padding: 4px 8px !important; min-height: 22px !important; height: 22px !important; border-radius: 3px !important; }
            .shipping-cancel-button { margin-right: 5px !important; }
          }
          @media (max-width: 480px) {
            .shipping-modal-overlay { padding: 3px !important; }
            .shipping-modal { width: calc(100vw - 6px) !important; max-width: calc(100vw - 6px) !important; max-height: calc(100dvh - 6px) !important; padding: 5px !important; }
            .shipping-modal-header { padding: 4px 6px !important; min-height: 26px !important; }
            .shipping-modal-title { font-size: 12px !important; }
            .shipping-modal-close { font-size: 15px !important; }
            .shipping-modal-content { padding: 4px !important; }
            .shipping-details-title { font-size: 10px !important; margin: 3px 0 !important; }
            .shipping-fields-container { padding: 4px !important; }
            .shipping-field { margin-bottom: 4px !important; }
            .shipping-field label { font-size: 8px !important; }
            .shipping-field input { font-size: 8px !important; min-height: 20px !important; height: 20px !important; padding: 2px 3px !important; margin-top: 1px !important; }
            .shipping-custom-dropdown { margin-top: 1px !important; }
            .shipping-dropdown-trigger { font-size: 8px !important; min-height: 20px !important; height: 20px !important; padding: 2px 20px 2px 3px !important; }
            .shipping-dropdown-arrow { right: 4px !important; font-size: 7px !important; }
            .shipping-dropdown-menu { top: calc(100% + 1px) !important; max-height: 145px !important; z-index: 999999 !important; }
            .shipping-dropdown-option { font-size: 8px !important; min-height: 19px !important; padding: 3px 4px !important; }
            .shipping-fields-container p { font-size: 7px !important; line-height: 1.1 !important; margin-top: 1px !important; margin-bottom: 1px !important; }
            .shipping-status { padding: 4px !important; margin-bottom: 4px !important; }
            .shipping-status p { font-size: 7px !important; line-height: 1.1 !important; }
            .shipping-modal-footer { margin-top: 5px !important; }
            .shipping-modal-button { font-size: 8px !important; min-height: 20px !important; height: 20px !important; padding: 3px 7px !important; }
            .shipping-cancel-button { margin-right: 4px !important; }
          }
          @media (max-width: 360px) {
            .shipping-modal { padding: 4px !important; }
            .shipping-modal-header { padding: 3px 5px !important; min-height: 24px !important; }
            .shipping-modal-title { font-size: 11px !important; }
            .shipping-modal-close { font-size: 14px !important; }
            .shipping-modal-content { padding: 3px !important; }
            .shipping-details-title { font-size: 9px !important; margin: 2px 0 !important; }
            .shipping-fields-container { padding: 3px !important; }
            .shipping-field { margin-bottom: 3px !important; }
            .shipping-field label { font-size: 7px !important; }
            .shipping-field input { font-size: 7px !important; height: 18px !important; min-height: 18px !important; padding: 1px 2px !important; margin-top: 1px !important; }
            .shipping-dropdown-trigger { font-size: 7px !important; height: 18px !important; min-height: 18px !important; padding: 1px 18px 1px 2px !important; }
            .shipping-dropdown-arrow { right: 3px !important; font-size: 6px !important; }
            .shipping-dropdown-menu { top: calc(100% + 1px) !important; max-height: 130px !important; z-index: 999999 !important; }
            .shipping-dropdown-option { font-size: 7px !important; min-height: 17px !important; padding: 2px 3px !important; }
            .shipping-fields-container p { font-size: 6.5px !important; }
            .shipping-status { padding: 3px !important; }
            .shipping-status p { font-size: 6.5px !important; }
            .shipping-modal-button { font-size: 7px !important; height: 18px !important; min-height: 18px !important; padding: 2px 5px !important; }
          }
        `}
      </style>

      {/* SHIPPING HEADING */}
      <div style={{ display: "flex", alignItems: "center", margin: "0 0 25px 0", padding: "0" }}>
        <h1 style={headingStyle}>Shipping</h1>
        <button
          className="shipping-edit-button"
          onClick={openModal}
          style={{
            cursor: "pointer",
            backgroundColor: "transparent",
            color: "#004aad",
            padding: "0",
            border: "none",
            outline: "none",
            boxShadow: "none",
            borderRadius: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "30px",
            width: "30px",
            minWidth: "30px",
            marginLeft: "10px",
            marginTop: "6px",
            boxSizing: "border-box",
          }}
        >
          <FaPencilAlt />
        </button>
      </div>

      {/* DISPLAY INFO */}
      <div style={sectionWrapperStyle}>
        <div style={labelStyle}>Ship To Account Date:</div>
        <div style={valueStyle}>{deliveryDate}</div>
      </div>
      <div style={sectionWrapperStyle}>
        <div style={labelStyle}>Shipper:</div>
        <div style={valueStyle}>{shipper}</div>
      </div>
      <div style={sectionWrapperStyle}>
        <div style={labelStyle}>Delivery Date:</div>
        <div style={valueStyle}>{deliveryDate}</div>
      </div>
      <div style={sectionWrapperStyle}>
        <div style={labelStyle}>Delivered Date and Time:</div>
        <div style={valueStyle}>{deliveredDateTime}</div>
      </div>
      <div style={sectionWrapperStyle}>
        <div style={labelStyle}>Track #:</div>
        <div style={valueStyle}>{trackNumberDisplay}</div>
      </div>
      <div style={sectionWrapperStyle}>
        <div style={labelStyle}>Shipping Cost:</div>
        <div style={valueStyle}>{shippingCostDisplay}</div>
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="shipping-modal-overlay">
          <div className="shipping-modal">
            <div className="shipping-modal-header">
              <h2 className="shipping-modal-title">Shipping</h2>
              <button onClick={closeModal} className="shipping-modal-close">
                ×
              </button>
            </div>

            <div className="shipping-modal-content">
              <h4 className="shipping-details-title">Shipping Details</h4>

              <div className="shipping-fields-container">
                {/* RESCHEDULE */}
                <div>
                  <div className="shipping-field">
                    <label>Reschedule?</label>
                    <div className="shipping-custom-dropdown" ref={rescheduleDropdownRef}>
                      <button
                        type="button"
                        className="shipping-dropdown-trigger"
                        onClick={() =>
                          setOpenDropdown(
                            openDropdown === "reschedule" ? null : "reschedule"
                          )
                        }
                      >
                        {selectedOption || "Keep Current"}
                        <span className="shipping-dropdown-arrow">▼</span>
                      </button>
                      {openDropdown === "reschedule" && (
                        <div className="shipping-dropdown-menu">
                          {rescheduleOptions.map((option) => (
                            <button
                              type="button"
                              key={option}
                              className={`shipping-dropdown-option ${
                                selectedOption === option ? "selected" : ""
                              }`}
                              onClick={() => handleOptionChange(option)}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {isOtherDateSelected && (
                    <div className="shipping-field">
                      <label>
                        Other Date <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        min={new Date().toISOString().split("T")[0]}
                      />
                    </div>
                  )}
                </div>

                {/* SHIPPER */}
                <div className="shipping-field">
                  <label>Shipper *</label>
                  <div className="shipping-custom-dropdown" ref={shipperDropdownRef}>
                    <button
                      type="button"
                      className="shipping-dropdown-trigger"
                      onClick={() =>
                        !shippingLoading &&
                        setOpenDropdown(
                          openDropdown === "shipper" ? null : "shipper"
                        )
                      }
                      disabled={shippingLoading}
                    >
                      {shippingLoading
                        ? "Loading shipping options..."
                        : selectedShipper}
                      <span className="shipping-dropdown-arrow">▼</span>
                    </button>
                    {openDropdown === "shipper" && !shippingLoading && (
                      <div className="shipping-dropdown-menu">
                        {shipperOptions.map((option) => (
                          <button
                            type="button"
                            key={option}
                            className={`shipping-dropdown-option ${
                              selectedShipper === option ? "selected" : ""
                            }`}
                            onClick={() => handleShipperChange(option)}
                          >
                            {option}
                            {shippingOptions.some(
                              (opt) => opt.label === option
                            ) &&
                              shippingOptions.find(
                                (opt) => opt.label === option
                              )?.price > 0 &&
                              ` (${formatAmount(
                                shippingOptions.find(
                                  (opt) => opt.label === option
                                ).price
                              )})`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="shipping-info-text">
                    Same Day. Last delivery by end of business day
                  </p>
                  <p className="shipping-info-text">
                    Delivery Date:{" "}
                    {selectedOption === "Ship Today"
                      ? format(new Date(), "dd MMM yyyy")
                      : selectedOption === "Ship Next Business Day"
                      ? format(
                          new Date(
                            new Date().setDate(new Date().getDate() + 1)
                          ),
                          "dd MMM yyyy"
                        )
                      : selectedOption === "Other Date" && selectedDate
                      ? format(parseISO(selectedDate), "dd MMM yyyy")
                      : selectedOption === "Other Date" && !selectedDate
                      ? "Select a date"
                      : deliveryDate}
                  </p>
                </div>

                {/* TRACK # */}
                <div className="shipping-field">
                  <label>Track #</label>
                  <input
                    type="text"
                    value={trackNumber}
                    onChange={handleTrackNumberChange}
                    placeholder="Enter tracking number"
                  />
                </div>

                {/* SHIPPING COST */}
                <div className="shipping-field">
                  <label>Shipping cost</label>
                  <input
                    type="number"
                    value={shippingCost}
                    onChange={handleShippingCostChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  {shippingCost !== "" && !isNaN(Number(shippingCost)) && (
                    <p className="shipping-cost-text">
                      Formatted: {formatAmount(Number(shippingCost))}
                    </p>
                  )}
                </div>

                {/* STATUS */}
                <div
                  className="shipping-status"
                  style={{
                    backgroundColor:
                      deliveredDateTime === "Not Delivered"
                        ? "#f8f9fa"
                        : "#e8f5e8",
                  }}
                >
                  <p
                    style={{
                      fontWeight: "bold",
                      margin: "0 0 5px 0",
                      color:
                        deliveredDateTime === "Not Delivered"
                          ? "#6c757d"
                          : "black",
                    }}
                  >
                    Current Delivery Status:
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color:
                        deliveredDateTime === "Not Delivered"
                          ? "#6c757d"
                          : "black",
                      fontWeight:
                        deliveredDateTime === "Not Delivered"
                          ? "normal"
                          : "bold",
                    }}
                  >
                    {deliveredDateTime}
                  </p>
                </div>
              </div>

              <div className="shipping-modal-footer">
                <button
                  onClick={closeModal}
                  className="shipping-modal-button shipping-cancel-button"
                >
                  Cancel
                </button>
                <button onClick={handleConfirm} className="shipping-modal-button">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shipping;
